import { Injectable } from '@angular/core';
import { firstValueFrom, map } from 'rxjs';
import { SearchApi } from '../../api/search.api';
import { timeDelay } from '../../helpers/time.helpers';
import { BrowseFilter } from '../../models/filter';
import { Sport } from '../../models/sport';

import { BrowseSorting } from '../../models/sorting';
import { Store } from '../../../../shared/store';
import { SearchedContentState } from './searched-content.state';
import { VideoSection, Coaching } from '@kinomap-dev/ng-ui-lib';
import { Playlist } from '@kinomap-dev/ng-ui-lib/lib/models/playlist.model';

const searchedContentState = new SearchedContentState();

@Injectable({ providedIn: 'root' })
export class SearchedContentStore extends Store<SearchedContentState> {
  searchedContents$ = this.state$.pipe(
    map((s) => {
      return s.searchedContents;
    })
  );
  isLoadingSearchedContents$ = this.state$.pipe(
    map((s) => s.isLoadingSearchedContents)
  );
  areSearchedContentsComplete$ = this.state$.pipe(
    map((s) => s.areSearchedContentsComplete)
  );
  searchedPlaylists$ = this.state$.pipe(map((s) => s.searchedPlaylists));
  isLoadingSearchedPlaylists$ = this.state$.pipe(
    map((s) => s.isLoadingSearchedPlaylists)
  );
  areSearchedPlaylistsComplete$ = this.state$.pipe(
    map((s) => s.areSearchedPlaylistsComplete)
  );

  constructor(private searchApi: SearchApi) {
    super(searchedContentState);
  }

  // SET DATA

  setSearchedContents(nextSearchedContents: VideoSection[] | Coaching[]): void {
    this.setState({ ...this.state, searchedContents: nextSearchedContents });
  }

  setIsLoadingSearchedContents(nextIsLoadingSearchedContents: boolean): void {
    this.setState({
      ...this.state,
      isLoadingSearchedContents: nextIsLoadingSearchedContents,
    });
  }

  setAreSearchedContentsComplete(
    nextAreSearchedContentsComplete: boolean
  ): void {
    this.setState({
      ...this.state,
      areSearchedContentsComplete: nextAreSearchedContentsComplete,
    });
  }

  setSearchedPlaylists(nextSearchedPlaylists: Playlist[]): void {
    this.setState({ ...this.state, searchedPlaylists: nextSearchedPlaylists });
  }

  setIsLoadingSearchedPlaylists(nextIsLoadingSearchedPlaylists: boolean): void {
    this.setState({
      ...this.state,
      isLoadingSearchedPlaylists: nextIsLoadingSearchedPlaylists,
    });
  }

  setAreSearchedPlaylistsComplete(
    nextAreSearchedPlaylistsComplete: boolean
  ): void {
    this.setState({
      ...this.state,
      areSearchedPlaylistsComplete: nextAreSearchedPlaylistsComplete,
    });
  }

  // GET DATA FROM THE API

  async getSearchedContents(
    {
      newSelectedSport,
      newSearchedTerm,
      newFilters,
      newSorting,
      isNextPage,
    }: {
      newSelectedSport?: Sport;
      newSearchedTerm?: string;
      newFilters?: BrowseFilter[];
      newSorting?: BrowseSorting;
      isNextPage: boolean;
    } = { isNextPage: false }
  ): Promise<void> {
    if (isNextPage && this.state.areSearchedContentsComplete) {
      return;
    }

    let goOn = !this.state.isLoadingSearchedContents;
    while (!goOn) {
      await timeDelay(100);
      goOn = !this.state.isLoadingSearchedContents;
    }

    if (!isNextPage) {
      this.setSearchedContents([]);
      this.setAreSearchedContentsComplete(false);
    }

    this.setIsLoadingSearchedContents(true);

    try {
      const fetchedSearchedContents: VideoSection[] | Coaching[] =
        await firstValueFrom(
          this.searchApi.getSearchedContents$(
            newSelectedSport,
            newSearchedTerm,
            newFilters,
            newSorting,
            isNextPage
          )
        );

      const newSearchedContents: VideoSection[] | Coaching[] =
        this.getNewSearchedContentsWithoutDuplicate(fetchedSearchedContents);

      this.setSearchedContents(newSearchedContents);

      this.setAreSearchedContentsComplete(
        this.searchApi.isContentsLastPageLoaded
      );
    } catch (err) {
      this.setAreSearchedContentsComplete(true);
    } finally {
      this.setIsLoadingSearchedContents(false);
    }
  }

  async getSearchedPlaylists({
    newSelectedSport,
    newSearchedTerm,
  }: {
    newSelectedSport?: Sport;
    newSearchedTerm?: string;
  } = {}): Promise<void> {
    if (
      !newSelectedSport &&
      !newSearchedTerm &&
      this.state.areSearchedPlaylistsComplete
    ) {
      return;
    }

    let goOn = !this.state.isLoadingSearchedPlaylists;
    while (!goOn) {
      await timeDelay(100);
      goOn = !this.state.isLoadingSearchedPlaylists;
    }

    if (!!newSelectedSport || !!newSearchedTerm) {
      this.setSearchedPlaylists([]);
      this.setAreSearchedPlaylistsComplete(false);
    }

    this.setIsLoadingSearchedPlaylists(true);

    try {
      const fetchedSearchedPlaylists: Playlist[] = await firstValueFrom(
        this.searchApi.getSearchedPlaylists$(newSelectedSport, newSearchedTerm)
      );
      const newSearchedPlaylists: Playlist[] =
        this.getNewSearchedPlaylistsWithoutDuplicate(fetchedSearchedPlaylists);
      this.setSearchedPlaylists(newSearchedPlaylists);

      this.setAreSearchedPlaylistsComplete(
        this.searchApi.isPlaylistsLastPageLoaded
      );
    } catch (err) {
      this.setAreSearchedPlaylistsComplete(true);
    } finally {
      this.setIsLoadingSearchedPlaylists(false);
    }
  }

  // PRIVATE

  private getNewSearchedContentsWithoutDuplicate(
    fetchedSearchedContents: VideoSection[] | Coaching[]
  ): VideoSection[] | Coaching[] {
    const newSearchedContentsWithDuplicates: (VideoSection | Coaching)[] =
      this.state?.searchedContents?.length > 0
        ? [...this.state.searchedContents, ...fetchedSearchedContents]
        : fetchedSearchedContents;

    const obj: {} = {};
    newSearchedContentsWithDuplicates.forEach(
      (content: VideoSection | Coaching) => (obj[content.id] = content)
    );
    return [
      ...new Set(
        newSearchedContentsWithDuplicates.map(
          (content: VideoSection | Coaching) => content.id
        )
      ),
    ].map((id) => obj[id]);
  }

  private getNewSearchedPlaylistsWithoutDuplicate(
    fetchedSearchedPlaylists: Playlist[]
  ): Playlist[] {
    const newSearchedPlaylistsWithDuplicates: Playlist[] = [
      ...this.state.searchedPlaylists,
      ...fetchedSearchedPlaylists,
    ];
    const obj: {} = {};
    newSearchedPlaylistsWithDuplicates.forEach(
      (playlist: Playlist) => (obj[playlist.id] = playlist)
    );
    return [
      ...new Set(
        newSearchedPlaylistsWithDuplicates.map(
          (playlist: Playlist) => playlist.id
        )
      ),
    ].map((id) => obj[id]);
  }
}
